import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "motion/react";
import { fadeInUp } from "./AnimationVariants";
import { TRUST_STATS, TrustStat } from "./trustStats";

const DURATION_MS = 1400;
const format = (n: number) => new Intl.NumberFormat("en-US").format(n);

function CountUp({ value, start }: { value: number; start: boolean }) {
  const [shown, setShown] = useState(0);

  useEffect(() => {
    if (!start) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setShown(value);
      return;
    }
    let raf = 0;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / DURATION_MS);
      setShown(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, value]);

  return <>{format(shown)}</>;
}

export function HeroStats() {
  const ref = useRef<HTMLDListElement>(null);
  const inView = useInView(ref, { once: true });
  const stats = TRUST_STATS.filter((s): s is TrustStat & { value: number } => s.value !== null);
  if (stats.length === 0) return null;

  return (
    <motion.div
      variants={fadeInUp}
      className="relative mt-8 w-full border-t border-transparent pt-[31px] before:absolute before:inset-x-0 before:-top-px before:h-px before:bg-[linear-gradient(to_right,transparent,rgba(255,255,255,0.18)_42%,rgba(255,255,255,0.18)_58%,transparent)]"
    >
      <p
        id="hero-stats-caption"
        className="mb-3 text-xs leading-none uppercase tracking-[0.12em] text-white/55"
      >
        Already training on Lemonade
      </p>
      <dl
        ref={ref}
        aria-labelledby="hero-stats-caption"
        className="flex flex-wrap justify-center gap-x-10 gap-y-4 lg:justify-start"
      >
        {stats.map((s) => (
          <div key={s.label} className="flex flex-col-reverse items-center lg:items-start">
            <dt className="text-xs uppercase tracking-[0.12em] text-white/55">{s.label}</dt>
            <dd className="text-3xl font-bold tabular-nums text-white">
              <span className="sr-only">
                {format(s.value)}
                {s.suffix}
              </span>
              <span aria-hidden="true">
                <CountUp value={s.value} start={inView} />
                {s.suffix && <span className="text-primary">{s.suffix}</span>}
              </span>
            </dd>
          </div>
        ))}
      </dl>
    </motion.div>
  );
}
